import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

/**
 * Placement for dropdown panels that render inline (absolutely positioned next
 * to their field) rather than portalling to the body.
 *
 * An inline panel is clipped by the nearest scrolling ancestor, not by the
 * viewport. Inside a dialog that ancestor is the scrollable body, whose bottom
 * edge sits right under the footer, so a panel that measures itself against the
 * window opens at full height and disappears behind the footer. Measuring the
 * box that actually clips it keeps the panel visible: it shrinks, or flips up
 * when there is more room above.
 */

/** Breathing room kept between the panel and the edge that clips it. */
const GUTTER = 8

/**
 * Below this the panel is cramped enough to be worth flipping up, provided
 * above is roomier.
 */
const FLIP_THRESHOLD = 160

/**
 * How often the open panel re-measures. One measurement at open time is not
 * enough: a popover anchors itself after mount, and focusing the field can
 * scroll the dialog body under it, both of which move the field after the
 * panel has picked a side.
 */
const MEASURE_INTERVAL_MS = 100

export interface InlineDropdownPlacement {
  /** Open upwards from the field instead of downwards. */
  above: boolean
  /** Height the panel may take on the chosen side, in pixels. */
  maxHeight: number
}

/**
 * Top and bottom edges of the box that will clip an inline dropdown: the
 * viewport, narrowed by every scrolling or hidden-overflow ancestor.
 */
function getClippingBounds(element: HTMLElement) {
  let top = 0
  let bottom = window.innerHeight
  let parent = element.parentElement
  while (parent && parent !== document.body) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY !== 'visible') {
      const rect = parent.getBoundingClientRect()
      top = Math.max(top, rect.top)
      bottom = Math.min(bottom, rect.bottom)
    }
    parent = parent.parentElement
  }
  return { top, bottom }
}

/**
 * Where an inline panel anchored to `field` should open, and how tall it may
 * be. `maxHeight` is the panel's preferred height, the cap it keeps when there
 * is room for it.
 */
export function getInlineDropdownPlacement(
  field: HTMLElement,
  maxHeight: number,
): InlineDropdownPlacement {
  const rect = field.getBoundingClientRect()
  const clip = getClippingBounds(field)
  const spaceAbove = rect.top - clip.top
  const spaceBelow = clip.bottom - rect.bottom
  // Only flip when doing so actually buys room; a field near the top of a short
  // dialog has no more space above it than below.
  const above = spaceBelow < FLIP_THRESHOLD && spaceAbove > spaceBelow
  // No minimum: a floor taller than the space that exists is exactly how these
  // panels ended up under a dialog footer in the first place.
  const available = Math.max(
    0,
    Math.min(maxHeight, (above ? spaceAbove : spaceBelow) - GUTTER),
  )
  return { above, maxHeight: available }
}

/**
 * Keeps an inline panel's placement in step with its field while the panel is
 * open: over the frames right after it opens (the anchor may still move), on
 * scroll and resize, and whenever the field itself changes size.
 */
export function useInlineDropdownPlacement(
  fieldRef: RefObject<HTMLElement | null>,
  { enabled, maxHeight }: { enabled: boolean; maxHeight: number },
): InlineDropdownPlacement {
  const [placement, setPlacement] = useState<InlineDropdownPlacement>({
    above: false,
    maxHeight,
  })

  useEffect(() => {
    if (!enabled) return

    const measure = () => {
      const field = fieldRef.current
      if (!field) return
      const next = getInlineDropdownPlacement(field, maxHeight)
      setPlacement(current =>
        current.above === next.above && current.maxHeight === next.maxHeight
          ? current
          : next,
      )
    }

    measure()
    // Polling rather than scroll/resize listeners: the moves that matter here
    // (a popover anchoring itself, a scroll container jumping to the focused
    // field, the field growing as chips are added) do not all raise an event
    // the panel can subscribe to. A rect read every 100ms while one dropdown
    // is open costs nothing, and re-renders only when the placement changes.
    const timer = window.setInterval(measure, MEASURE_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [enabled, maxHeight, fieldRef])

  return placement
}
